import React from "react";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { useFrame } from "../frame";
import {
    REDUCED_MOTION_QUERY,
    usePrefersReducedMotion,
} from "../reducedMotion";

import styles from "./index.module.scss";

// The prototype's exit threshold, where a section doesn't set its own
const DEFAULT_HYSTERESIS = 0.12;

export interface RevealOptions<T extends Element> {
    /**
     * Reveal once the target's top is above this fraction of the viewport
     * height (and its bottom is below the viewport's top). Work header 0.78,
     * Work cards 0.86, ticker 0.92, Proof head 0.80, Proof rows 0.84.
     */
    enter: number;
    /**
     * Re-arm once the target's top falls below this fraction of the viewport
     * height. Only a bottom exit re-arms: something that left through the top
     * keeps its finished state. Defaults to `enter + 0.12`.
     */
    exit?: number;
    /**
     * Milliseconds from the gate opening to each item's reveal. One entry per
     * item; `shown` has the same length. Defaults to `[0]`.
     */
    delays?: readonly number[];
    /**
     * The element to measure, given the ref's element. Defaults to the ref's
     * element itself.
     */
    target?: (root: T) => Element | null;
    /**
     * Measure the target where it rests, discounting its own translateY. For a
     * target that is itself armed (`mask-self`), whose 104% drop would
     * otherwise push every threshold down by its own height.
     */
    resting?: boolean;
}

export interface Reveal<T extends Element> {
    /** Attach to the element the gate measures (or its root; see `target`). */
    ref: React.RefObject<T | null>;
    /** Per item, whether it is revealed. Pass to `Mask` or `revealClassName`. */
    shown: boolean[];
}

const filled = (length: number, value: boolean) =>
    Array.from({ length }, () => value);

/**
 * A scroll gate that reveals a sequence of items, ported from the three copies
 * in the prototype (featuredwork.js, expertise.js, proof.js).
 *
 * Content renders visible (server render, no JS) and is armed in a layout
 * effect, so if anything fails the copy is visible rather than stuck hidden.
 * The target is measured with getBoundingClientRect on the shared frame loop,
 * as the prototype does, so thresholds match it exactly, while an
 * IntersectionObserver reports it near the viewport. An open gate is also
 * checked on every scroll event, wherever it is, so a jump past the band still
 * re-arms it. Reduced motion shows everything at once.
 */
export const useReveal = <T extends Element = HTMLDivElement>(
    options: RevealOptions<T>,
): Reveal<T> => {
    const ref = React.useRef<T>(null);
    const optionsRef = React.useRef(options);
    React.useLayoutEffect(() => {
        optionsRef.current = options;
    });

    const count = options.delays?.length ?? 1;
    const reduced = usePrefersReducedMotion();
    const active = !reduced;
    // Poll on frames only while the target is within half a viewport of the
    // screen, so the shared loop can idle. The margin covers the exit
    // threshold; jumps across it are caught by the scroll check below.
    const [near, setNear] = React.useState(false);

    const [shown, setShown] = React.useState(() => filled(count, true));
    const gate = React.useRef({
        on: false,
        cold: true,
        held: false,
        frames: 0,
    });
    const timers = React.useRef<number[]>([]);

    const clearTimers = () => {
        timers.current.forEach((id) => window.clearTimeout(id));
        timers.current = [];
    };

    React.useLayoutEffect(() => {
        // Read the media query directly: the hook's state is still false on
        // the first client render, and arming here would flash the content.
        const reducedNow = window.matchMedia(REDUCED_MOTION_QUERY).matches;
        gate.current = { on: false, cold: true, held: false, frames: 0 };
        clearTimers();
        // Arming has to follow the first render, which matches the server's
        // (visible) markup, and has to land before paint.
        if (active && !reducedNow) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setShown(filled(count, false));
        } else {
            setShown(filled(count, true));
        }
        return clearTimers;
    }, [active, count]);

    React.useEffect(() => {
        const root = ref.current;
        if (!active || !root) return;
        // Watch the root, which holds the target: an armed `mask-self` target
        // is clipped to nothing, and would never report as intersecting
        const observer = new IntersectionObserver(
            (entries) => setNear(entries[entries.length - 1].isIntersecting),
            { rootMargin: "50% 0px" },
        );
        observer.observe(root);
        return () => observer.disconnect();
    }, [active]);

    React.useEffect(() => {
        const root = ref.current;
        if (!active || !root) return;
        // Focus landing inside an armed gate (Tab onto a hidden CTA) opens it
        // at once, with no stagger, so the focused element is never invisible.
        // Held like a cold opening: the frame loop drops the hold once the
        // target is above the exit line, and only a bottom exit re-arms.
        const onFocusIn = () => {
            const g = gate.current;
            if (!g.on) g.held = true;
            g.on = true;
            clearTimers();
            setShown((prev) =>
                prev.every(Boolean) ? prev : filled(count, true),
            );
        };
        root.addEventListener("focusin", onFocusIn);
        return () => root.removeEventListener("focusin", onFocusIn);
    }, [active, count]);

    const evaluate = () => {
        const g = gate.current;
        const root = ref.current;
        if (!root) return;
        const { enter, exit, delays, target, resting } = optionsRef.current;
        const el = target ? target(root) : root;
        if (!el) return;

        const rect = el.getBoundingClientRect();
        const shift = resting
            ? new DOMMatrixReadOnly(getComputedStyle(el).transform).m42
            : 0;
        const top = rect.top - shift;
        const bottom = rect.bottom - shift;
        const vh = window.innerHeight;
        // The first evaluation reveals anything on screen at all: the hysteresis
        // band would otherwise leave an on-screen element armed after a reload.
        const enterAt = g.cold ? 1 : enter;
        // A cold opening below the exit line holds until the target leaves
        // through the viewport's bottom, or the next frame would re-arm it.
        // Once it has been above the line, the normal exit applies.
        const exitLine = exit ?? enter + DEFAULT_HYSTERESIS;
        if (top <= vh * exitLine) g.held = false;
        const exitAt = g.held ? Math.max(exitLine, 1) : exitLine;

        if (!g.on && top < vh * enterAt && bottom > 0) {
            g.on = true;
            g.held = g.cold && top > vh * exitLine;
            clearTimers();
            (delays ?? [0]).forEach((delay, i) => {
                timers.current.push(
                    window.setTimeout(() => {
                        setShown((prev) =>
                            prev.map((value, j) => (j === i ? true : value)),
                        );
                    }, delay),
                );
            });
        } else if (g.on && top > vh * exitAt) {
            g.on = false;
            g.held = false;
            clearTimers();
            setShown(filled(count, false));
        }
        g.cold = false;
    };
    const evaluateRef = React.useRef(evaluate);
    React.useLayoutEffect(() => {
        evaluateRef.current = evaluate;
    });

    React.useEffect(() => {
        if (!active) return;
        // A jump (Home, scroll restoration) can carry an open gate from above
        // the band to below it between two frames, never crossing the band
        // the observer watches. Checking on scroll too, as the prototype does,
        // still sees it pass the exit line.
        const onScroll = () => {
            if (gate.current.on) evaluateRef.current();
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, [active]);

    useFrame(() => {
        // The first frame runs before the armed state has been painted;
        // revealing on it would skip the animation.
        if (gate.current.frames++ < 1) return;
        evaluate();
    }, active && near);

    return { ref, shown };
};

/**
 * Class names for a block that fades in and rises (the prototype's
 * `[data-rv]` and `.card`), armed while `shown` is false.
 */
export const revealClassName = (shown: boolean) =>
    concatClassNames([styles.fade, shown ? "" : styles.pre]);

interface MaskProps {
    /** False arms it (hidden below its box); true slides it up into place. */
    shown: boolean;
    /** Adds the clip padding a CTA pill needs so its edge isn't cut off. */
    cta?: boolean;
    className?: string;
    children: React.ReactNode;
}

/**
 * The prototype's `.mask > span`: the content slides up into an
 * overflow-hidden box. A span with block display, so it can sit inside a
 * heading or paragraph.
 */
export const Mask = (props: MaskProps) => (
    <span
        className={concatClassNames([
            styles.mask,
            props.cta ? styles.maskCta : "",
            props.shown ? "" : styles.pre,
            props.className,
        ])}
    >
        <span>{props.children}</span>
    </span>
);
